<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">IMEI Numbers</h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by IMEI Number, User Name, or Phone..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="2">
                  <v-autocomplete
                    solo
                    rounded
                    ref="status"
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    ref="position"
                    v-model="filter.position_id"
                    :items="positions"
                    label="Position"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-account"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    ref="masterDevice"
                    v-model="filter.master_device"
                    :items="masterDeviceOptions"
                    label="Master Device"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-key-variant"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table v-else class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">User Name</th>
                      <th class="text-left">Phone</th>
                      <th class="text-left">Email</th>
                      <th class="text-left">Position</th>
                      <th class="text-left">IMEI Number</th>
                      <th class="text-left">Master Device</th>
                      <th class="text-left">Created At</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="imeiDevices.length != 0">
                    <tr v-for="(device, i) in imeiDevices" :key="`device${i}`">
                      <td style="min-width: 85px">
                        {{ serialNoStarting + i + 1 }}
                      </td>
                      <td>
                        <v-tooltip bottom v-if="device.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Device is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="device.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Device is InActive</span>
                        </v-tooltip>
                      </td>
                      <td>
                        {{ device.user ? device.user.first_name + ' ' + (device.user.last_name || '') : 'N/A' }}
                      </td>
                      <td>{{ device.user ? device.user.phone : 'N/A' }}</td>
                      <td>{{ device.user ? device.user.email : 'N/A' }}</td>
                      <td>
                        {{ device.user && device.user.position ? device.user.position.name : 'N/A' }}
                      </td>
                      <td>
                        <v-chip
                          :color="device.is_active ? 'teal' : 'red'"
                          text-color="white"
                          small
                        >
                          {{ device.imei_no }}
                        </v-chip>
                      </td>
                      <td class="text-center" style="vertical-align: middle;">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-switch
                              v-bind="attrs"
                              v-on="on"
                              v-model="device.master_device"
                              @change="toggleMasterDevice(device)"
                              :loading="device.updating"
                              color="orange"
                              inset
                              dense
                              hide-details
                              class="my-0 py-0"
                              style="justify-content: center;"
                            ></v-switch>
                          </template>
                          <span>{{ device.master_device ? 'Can login with any credentials' : 'Normal device restrictions' }}</span>
                        </v-tooltip>
                      </td>
                      <td style="min-width: 130px">
                        {{ formatDate(device.created_at) }}
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto"
                              color="info"
                              @click="viewDevice(device)"
                            >
                              <v-icon dark> mdi-eye </v-icon>
                            </v-btn>
                          </template>
                          <span>View device details</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="ml-2"
                              fab
                              x-small
                              color="primary"
                              @click="editDevice(device)"
                            >
                              <v-icon dark> mdi-pencil </v-icon>
                            </v-btn>
                          </template>
                          <span>Edit this device</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto ml-2"
                              color="error"
                              @click="confirmDelete(device)"
                            >
                              <v-icon dark> mdi-delete </v-icon>
                            </v-btn>
                          </template>
                          <span>Delete this device</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Device Details Dialog -->
    <v-dialog v-model="deviceDialog" max-width="600px" persistent>
      <v-card v-if="selectedDevice">
        <v-card-title>
          <h3>Device Details</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="deviceDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <h4 class="mb-3">User Information</h4>
                <v-simple-table>
                  <tbody>
                    <tr>
                      <td><strong>Name:</strong></td>
                      <td>{{ selectedDevice.user ? selectedDevice.user.first_name + ' ' + (selectedDevice.user.last_name || '') : 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td><strong>Phone:</strong></td>
                      <td>{{ selectedDevice.user ? selectedDevice.user.phone : 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td><strong>Email:</strong></td>
                      <td>{{ selectedDevice.user ? selectedDevice.user.email : 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td><strong>Position:</strong></td>
                      <td>{{ selectedDevice.user && selectedDevice.user.position ? selectedDevice.user.position.name : 'N/A' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <h4 class="mb-3">Device Information</h4>
                <v-simple-table>
                  <tbody>
                    <tr>
                      <td><strong>IMEI Number:</strong></td>
                      <td>{{ selectedDevice.imei_no }}</td>
                    </tr>
                    <tr>
                      <td><strong>Status:</strong></td>
                      <td>
                        <v-chip
                          :color="selectedDevice.is_active ? 'green' : 'red'"
                          dark
                          small
                        >
                          {{ selectedDevice.is_active ? 'Active' : 'Inactive' }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Master Device:</strong></td>
                      <td>
                        <v-chip
                          :color="selectedDevice.master_device ? 'orange' : 'gray'"
                          dark
                          small
                        >
                          {{ selectedDevice.master_device ? 'Yes' : 'No' }}
                        </v-chip>
                        <br>
                        <small class="text--secondary" v-if="selectedDevice.master_device">
                          This device can login with any user credentials
                        </small>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Created At:</strong></td>
                      <td>{{ formatDateTime(selectedDevice.created_at) }}</td>
                    </tr>
                    <tr>
                      <td><strong>Updated At:</strong></td>
                      <td>{{ formatDateTime(selectedDevice.updated_at) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Device Dialog -->
    <v-dialog v-model="editDialog" max-width="500px" persistent>
      <v-card v-if="editingDevice">
        <v-card-title>
          <h3>Edit Device</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeEditDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="editForm" v-model="editValid">
            <v-text-field
              v-model="editingDevice.imei_no"
              label="IMEI Number"
              :rules="[v => !!v || 'IMEI Number is required']"
              outlined
              dense
            ></v-text-field>
            <v-switch
              v-model="editingDevice.is_active"
              label="Active"
              inset
            ></v-switch>
            <v-switch
              v-model="editingDevice.master_device"
              label="Master Device (Can login with any credentials)"
              color="orange"
              inset
            ></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" text @click="closeEditDialog">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="updateDevice"
            :loading="updating"
            :disabled="!editValid"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <ConfirmationDialog
      :show="confirmDialog"
      title="Delete Device"
      message="Are you sure you want to delete this device record?"
      icon="mdi-delete"
      color="error"
      @confirm="deleteDevice"
      @update:show="confirmDialog = $event"
    />
  </v-main>
</template>

<script>
import axios from "axios";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";

export default {
  name: "ImeiNumbers",
  components: {
    ConfirmationDialog
  },
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        position_id: "",
        master_device: ""
      },
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      imeiDevices: [],
      statuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: ""
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" }
      ],
      positions: [
        {
          id: 0,
          text: "Select Position",
          value: ""
        }
      ],
      masterDeviceOptions: [
        {
          id: 0,
          text: "All Devices",
          value: ""
        },
        { id: "MASTER", text: "Master Devices", value: "1" },
        { id: "NORMAL", text: "Normal Devices", value: "0" }
      ],
      isLoading: false,
      updating: false,
      deviceDialog: false,
      editDialog: false,
      confirmDialog: false,
      selectedDevice: null,
      editingDevice: null,
      editValid: false,
      deviceToDelete: null
    };
  },
  watch: {
    page: "getData"
  },
  mounted() {
    this.getData();
    this.getMasters();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`users/masters`)
        .then((masters) => {
          masters = masters.data;
          // Position
          masters.positions.forEach((position) => {
            this.positions.push({
              id: position.id,
              text: position.name,
              value: position.id
            });
          });
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    async getData() {
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.position_id = this.filter.position_id
        ? this.filter.position_id
        : "";
      this.filter.master_device = this.filter.master_device
        ? this.filter.master_device
        : "";
      
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&is_active=" +
        this.filter.is_active +
        "&position_id=" +
        this.filter.position_id +
        "&master_device=" +
        this.filter.master_device +
        "&search_keyword=" +
        this.filter.search_keyword;
        
      await axios
        .get(`user_devices?${query}`)
        .then((response) => {
          this.imeiDevices = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    viewDevice(device) {
      this.selectedDevice = device;
      this.deviceDialog = true;
    },
    editDevice(device) {
      this.editingDevice = { ...device };
      this.editDialog = true;
    },
    closeEditDialog() {
      this.editDialog = false;
      this.editingDevice = null;
      this.$refs.editForm && this.$refs.editForm.resetValidation();
    },
    async updateDevice() {
      if (!this.$refs.editForm.validate()) return;

      this.updating = true;
      try {
        const response = await axios.patch(
          `/user_devices/${this.editingDevice.id}`,
          {
            imei_no: this.editingDevice.imei_no,
            is_active: this.editingDevice.is_active
          }
        );

        if (response.status === 200) {
          this.closeEditDialog();
          this.getData();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.updating = false;
      }
    },
    async toggleMasterDevice(device) {
      // Add updating flag to show loading state
      this.$set(device, 'updating', true);
      
      try {
        const response = await axios.post('/user_devices/toggle_master', {
          device_id: device.id,
          master_device: device.master_device
        });

        if (response.data.success) {
          // Update device data with response
          Object.assign(device, response.data.data);
          
          // Show success message
          const status = device.master_device ? 'enabled' : 'disabled';
          this.$toast.success(`Master device access ${status} for IMEI: ${device.imei_no}`);
        }
      } catch (error) {
        // Revert the switch on error
        device.master_device = !device.master_device;
        console.log(error);
        
        if (error.response && error.response.data && error.response.data.message) {
          this.$toast.error(error.response.data.message);
        } else {
          this.$toast.error('Failed to update master device status');
        }
      } finally {
        this.$set(device, 'updating', false);
      }
    },
    confirmDelete(device) {
      this.deviceToDelete = device;
      this.confirmDialog = true;
    },
    async deleteDevice() {
      if (!this.deviceToDelete) return;

      try {
        const encryptedId = this.encryptIt(this.deviceToDelete.id.toString());
        const response = await axios.post(
          `/user_devices/delete/${encryptedId}`
        );

        if (response.status === 200 && response.data.success) {
          this.getData();
          this.$toast.success(response.data.message || 'Device deleted successfully');
        }
      } catch (error) {
        console.log(error);
        if (error.response && error.response.data && error.response.data.message) {
          this.$toast.error(error.response.data.message);
        } else {
          this.$toast.error('Failed to delete device');
        }
      } finally {
        this.confirmDialog = false;
        this.deviceToDelete = null;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleTimeString();
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    }
  }
};
</script>

<style scoped>
.v-data-table {
  border-radius: 16px;
}
</style>